import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { FileSearch, ClipboardList, ScanEye, Zap } from "lucide-react";

const evals = [
  {
    icon: FileSearch,
    title: "Evaluaciones de Lenguaje",
    desc: "Diagnóstico integral del desarrollo del lenguaje, habla, fluidez verbal y habilidades comunicativas mediante instrumentos estandarizados.",
    color: "#1a6b7c",
  },
  {
    icon: ClipboardList,
    title: "Evaluaciones del Neurodesarrollo Integrales",
    desc: "Evaluación multidimensional de las áreas cognitiva, motriz, sensorial, emocional y social para obtener un perfil completo del desarrollo.",
    color: "#1e3a5f",
  },
  {
    icon: ScanEye,
    title: "Evaluaciones de Autismo",
    desc: "Diagnóstico especializado del Trastorno del Espectro Autista (TEA) usando instrumentos validados internacionalmente para detección temprana y oportuna.",
    color: "#5a9a8a",
  },
  {
    icon: Zap,
    title: "Evaluaciones de TDAH",
    desc: "Evaluación multimodal del Trastorno por Déficit de Atención e Hiperactividad con informes detallados orientados a la intervención.",
    color: "#1a6b7c",
  },
];

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };
const cardAnim = {
  hidden:  { opacity: 0, x: -18 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

export default function Evaluations() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="evaluaciones"
      ref={ref}
      className="py-24 bg-white"
      aria-labelledby="eval-heading"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-14"
        >
          <p className="font-sans text-xs font-semibold tracking-widest text-primary uppercase mb-3">
            Evaluaciones Diagnósticas
          </p>
          <h2 id="eval-heading" className="section-heading text-3xl sm:text-4xl mb-4">
            Diagnóstico preciso para la intervención adecuada
          </h2>
          <div className="w-12 h-0.5 bg-primary rounded-full" aria-hidden="true" />
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid sm:grid-cols-2 gap-5"
          role="list"
        >
          {evals.map(({ icon: Icon, title, desc, color }) => (
            <motion.article
              key={title}
              variants={cardAnim}
              role="listitem"
              className="group flex gap-5 bg-white rounded-xl p-6 border border-border hover:shadow-cardHov transition-shadow duration-200 overflow-hidden relative"
            >
              {/* Left border accent */}
              <div
                className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl"
                style={{ background: color }}
                aria-hidden="true"
              />

              {/* Icon */}
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 transition-transform duration-200 group-hover:scale-105"
                style={{ background: `${color}10` }}
                aria-hidden="true"
              >
                <Icon size={20} style={{ color }} strokeWidth={1.75} />
              </div>

              <div>
                <h3 className="font-serif font-bold text-[1rem] text-navy mb-2 leading-snug">
                  {title}
                </h3>
                <p className="font-sans text-sm text-txtMuted leading-relaxed">
                  {desc}
                </p>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
